import Transition from "@shared/core/Transition";
import { useEffect, useRef, useState } from "react";

interface FilterOption {
  value: string;
  label: string;
}

interface DropdownFilterProps {
  align?: "left" | "right";
  options: FilterOption[];
  onChange: (value: string) => void;
  onClose?: () => void;
}

export default function DropdownFilter({
  align = "right",
  options,
  onChange,
  onClose,
}: DropdownFilterProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState<FilterOption>(options[0]);
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const handleSelect = (option: FilterOption) => {
    setSelected(option);
    onChange(option.value);
    setDropdownOpen(false);
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="btn bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 hover:border-slate-300 text-slate-500 hover:text-slate-600"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Filter</span>
        <wbr />
        <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
          <path d="M9 15H7a1 1 0 010-2h2a1 1 0 010 2zM11 11H5a1 1 0 010-2h6a1 1 0 010 2zM13 7H3a1 1 0 010-2h10a1 1 0 010 2zM15 3H1a1 1 0 010-2h14a1 1 0 010 2z" />
        </svg>
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className={`origin-top-right z-10 absolute top-full min-w-56 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 pt-1.5 rounded shadow-lg overflow-hidden mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        unmountOnExit={true}
      >
        <div ref={dropdown}>
          <div className="text-xs font-semibold text-slate-400 dark:text-gray-400 uppercase pt-1.5 pb-2 px-4">
            Filter
          </div>
          <ul className="mb-4">
            {options.map((option) => (
              <li key={option.value} className="py-1 px-3">
                <button
                  className={`font-medium text-sm rounded w-full text-left p-2 hover:bg-slate-50 dark:hover:bg-gray-700 ${
                    option.value === selected.value
                      ? "text-yellow-700"
                      : "text-slate-600"
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Transition>
    </div>
  );
}
